import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import zipcelx from 'zipcelx';
import { cloneDeep } from 'lodash';
import Highcharts from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import Humanize from 'humanize-plus';
import styled from 'styled-components';
import reactGA from 'react-ga';
import CustomEvents from 'highcharts-custom-events';
import moment from 'moment';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';
import { searchToObj } from '~/shared/utils';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { formatTooltipHtml } from '~/shared/utils';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
HCAccessibility(Highcharts);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

interface ISeriesObj {
  name: string;
  data: string[];
  color: string | undefined;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => ($gridheight / 4) * 470 + ($gridheight / 4 - 1) * 23}px;
  width: 100%;
  position: relative;
  margin-top: 10px;
  border-bottom: 1px solid black;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-series-0 > .highcharts-point {
    transform: translate(0px, 5px) !important;
  }
  .highcharts-series-1 > .highcharts-point {
    transform: translate(0px, 0px) !important;
  }
  .highcharts-legend-item > .highcharts-point {
    transform: unset !important;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
  .highcharts-indicator-menu {
    max-width: 460px;
    right: 0px !important;
    left: unset !important;
  }
`;

Highcharts.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function HorizontalStackedBarChart({
  data,
  size,
  onClick,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight = 4,
  gridIndex,
  gridPrism,
  chartYmax,
  chartYmin,
}: any) {
  const chartRef = useRef<any>(null);
  const location = useLocation();
  const hasAnimatedRef = useRef(false);
  const chartInitializedRef = useRef(false);
  const sizeReadyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isSizeStable, setIsSizeStable] = useState(false);
  const [cameFromDrawer, setCameFromDrawer] = useState(location.state?.fromDrawer === true);

  useEffect(() => {
    const fromDrawer = location.state?.fromDrawer === true;

    setCameFromDrawer(prev => {
      if (prev === fromDrawer) {
        return prev;
      }
      return fromDrawer;
    });
  }, [location.key, location.state?.fromDrawer]);

  useEffect(() => {
    return () => {
      if (sizeReadyTimeoutRef.current) {
        clearTimeout(sizeReadyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (chartInitializedRef.current) {
      return;
    }

    if (sizeReadyTimeoutRef.current) {
      clearTimeout(sizeReadyTimeoutRef.current);
    }

    if (!size?.width || size.width <= 0) {
      setIsSizeStable(false);
      return;
    }

    setIsSizeStable(false);

    sizeReadyTimeoutRef.current = setTimeout(() => {
      setIsSizeStable(true);
    }, 150);
  }, [size?.width]);

  const queryStringParams = useMemo(() => searchToObj(window.location.search), []);

  const textBoldShadow = '0 0 .01px black, 0 0 .01px black, 0 0 .01px black';

  const [firstLoad, setFirstLoad] = useState(true);

  const [selectedLabel, setSelectedLabel] = useState(false);
  const [labelClicked, setLabelClicked] = useState(false);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    setTimeout(() => setFirstLoad(false), 50);
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  useEffect(() => {
    if (!chartInitializedRef.current) {
      return;
    }

    if (size?.width && size.width > 0) {
      chartRef.current?.reflow();
    }
  }, [chartRef, size]);

  const chartMemo = useMemo(() => {
    if (!size?.width || size.width <= 0) {
      return undefined;
    }

    if (!chartInitializedRef.current && !isSizeStable) {
      return undefined;
    }

    const sortedData: IStackBarChart = cloneDeep({
      axisYLabels: data?.row_nodes?.map((r: any) => r.name),
      axisXLabels: data?.col_nodes?.map((c: any) => c.name),
      matrix: data?.data,
      data: data,
      values: [],
    });

    let maxValue = 0;

    const categoriesData: string[] = [];
    const seriesData: any[] = [];

    for (let j = 0; j < sortedData.axisXLabels?.length; ++j) {
      const seriesObj: ISeriesObj = {
        name: sortedData.axisXLabels[sortedData.axisXLabels?.length - j - 1],
        data: [],
        color: colorScheme ? colorScheme[sortedData.axisXLabels?.length - j - 1] : undefined,
      };
      for (let i = 0; i < sortedData.axisYLabels?.length; ++i) {
        if (j === 0) categoriesData.push(sortedData.axisYLabels[i]);
        const objData = data.data[i][sortedData.axisXLabels?.length - j - 1] as unknown as string;
        seriesObj.data.push(objData);
      }
      seriesData.push({ ...seriesObj, data: seriesObj.data.map((d: any) => (d === 0 ? d : d + maxValue * 0.06)) });
    }

    data?.data?.forEach((d: any) => {
      if (d[1] + d[0] > maxValue) maxValue = d[1] + d[0];
    });

    if (!data || !seriesData || seriesData.length === 0) {
      return undefined;
    }

    let shouldAnimate = false;
    if (!hasAnimatedRef.current) {
      if (!cameFromDrawer) {
        shouldAnimate = true;
      }
      hasAnimatedRef.current = true;
    }

    const animationConfig: Highcharts.AnimationOptionsObject | boolean = shouldAnimate ? { duration: 800 } : false;

    if (!chartInitializedRef.current) {
      chartInitializedRef.current = true;
    }

    return {
      options: {
        chart: {
          className: prism.replace(' ', ''),
          width: size.width - 32,
          spacingRight: 20,
          marginLeft: 162,
          height: (gridHeight / 4) * 454 + (gridHeight / 4 - 1) * 40,
          type: 'bar',
          animation: animationConfig,
          style: {
            fontFamily: ['Montserrat', 'sans-serif'],
            left: '0px',
          },
        },
        exporting: {
          filename: exportFileName,
          csv: {
            columnHeaderFormatter: function (item: any) {
              if (item && item instanceof Highcharts.Axis) {
                if ((item as unknown as any).type === 'datetime') {
                  return 'Date';
                }
                return 'Category';
              }
              return {
                columnTitle: item.name,
              };
            },
            dateFormat: '%m/%d/%Y',
          },
          enabled: true,
          buttons: {
            contextButton: {
              x: 18,
              y: 2,
              symbol: 'ellipsisvertical',
              menuClassName: 'highcharts-indicator-menu',
              menuItems: [
                'viewFullscreen',
                'downloadPNG',
                'downloadSVG',
                'downloadPDF',
                'separator',
                'downloadXLS',
                'downloadCSV',
              ],
            },
          },
          fallbackToExportServer: false,
        },
        navigation: {
          x: -100,
          menuItemHoverStyle: {
            background: '#EAF2FF',
          },
          menuItemStyle: {
            height: 30,
            fill: '#33485E',
            fontSize: 15,
            padding: '6px 12px',
          },
        },
        title: {
          text: chartTitle,
          align: 'left',
          x: 12,
          y: 24,
          style: {
            fontSize: '18px',
            color: 'var(--color-chart-title)',
            fontWeight: 500,
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        subtitle: {
          text: chartSubtitle,
          align: 'left',
          x: 12,
          y: 49,
          style: {
            color: 'var(--color-chart-title)',
            fontWeight: 'normal',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        credits: {
          enabled: false,
        },
        xAxis: {
          categories: categoriesData,
          zIndex: 1,
          labels: {
            formatter: function (): string {
              const obj = this as unknown as any;
              return `<div data-id='${`clickableLabel${prism.replace(' ', '')}${gridIndex}`}' id='${
                data.row_nodes[obj.pos].id
              }#${data.title}' data-name='${obj.value}' style='text-shadow:${
                data?.row_nodes?.filter((c: any) => String(selectedLabel).includes(c.id))[0]?.name === obj.value &&
                prism === gridPrism
                  ? textBoldShadow
                  : 'none'
              }' class="clickableLabel">${obj.value.length > 16 ? `${obj.value.substring(0, 13)}...` : obj.value}</div>`;
            },
            events: {
              click: function (e: any) {
                const cellInfo = {
                  source: 'value',
                  topic: e.target.id.split('#')[0],
                };
                const chart = chartRef.current;
                const usesId = e.target.id.split('#')[0].includes(': ');
                const objOrg = data.row_nodes.filter((l: any) => l.id === e.target.id.split('#')[0]);
                const description = objOrg[0]?.description;
                if (
                  chart.xAxisTooltip &&
                  ((usesId ? e.target.id.split('#')[0].split(': ')[1] : e.target.id.split('#')[0]).length > 16 ||
                    description)
                ) {
                  chart.xAxisTooltip.destroy();
                  chart.xAxisTooltipArrow.destroy();
                  chart.xAxisTooltipArrowBlocker.destroy();
                  chart.xAxisTooltip = undefined;
                }
                if (e.target.id === queryStringParams.Narrative) return;

                const elements = document.getElementsByClassName('clickableLabel');

                for (let i = 0; i < elements.length; i++) {
                  (elements[i] as unknown as any).style.textShadow = 'none';
                }

                setSelectedLabel(e.target.id);
                setLabelClicked(true);
                onClick(cellInfo);
              },
              mouseover: function () {
                if (labelClicked || firstLoad) return;
                const chartBox = document.querySelector(`.${prism.replace(' ', '')}`);
                const chartRect = chartBox && chartBox.getBoundingClientRect();
                const obj = this as unknown as any;
                const value = obj.value.split("'")[3];
                const name = obj.value.split("'")[5];
                const objBox = document.querySelector(`[id="${value}"`);
                const objRect = objBox && objBox.getBoundingClientRect();
                const objOrg = data.row_nodes.filter((l: any) => l.id === value.split('#')[0]);
                const description = objOrg[0]?.description;
                const chart = chartRef.current;
                if (chart.xAxisTooltip !== undefined) chart.xAxisTooltip = undefined;
                const tooltipHtml = formatTooltipHtml({
                  title: name,
                  description,
                  tooltipCfg,
                  letterSpacingAdjustment: -0.75,
                  titleExtraStyles: 'min-width:160px',
                  descriptionDir: 'ltr',
                });
                chart.xAxisTooltip = chart.renderer
                  .label(tooltipHtml, 0, 0, 'rectangle', undefined, undefined, true)
                  .css({
                    color: tooltipCfg.color,
                    width: tooltipCfg.maxWidth,
                    opacity: 0,
                  })
                  .attr({
                    fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                    filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.35))',
                    'dominant-baseline': 'auto',
                    'stroke-width': tooltipCfg.borderWidth,
                    'stroke-dasharray':
                      tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                    'stroke-opacity': tooltipCfg.opacity,
                    padding: Number(tooltipCfg.padding.replace('px', '')),
                    r: Number(tooltipCfg.borderRadius.replace('px', '')),
                    class: 'xLabel',
                  })
                  .add()
                  .toFront();
                chart.xAxisTooltipArrow = chart.renderer
                  .label(``, 0, 0, 'rectangle')
                  .css({
                    color: tooltipCfg.color,
                    width: tooltipCfg.maxWidth,
                    opacity: 0,
                  })
                  .attr({
                    fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                    height: 4,
                    width: 4,
                    'dominant-baseline': 'auto',
                    'stroke-width': tooltipCfg.borderWidth,
                    'stroke-dasharray':
                      tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                    'stroke-opacity': tooltipCfg.opacity,
                    padding: 2,
                    r: 0,
                    class: 'xLabelArrow',
                  })
                  .add()
                  .toFront();
                chart.xAxisTooltipArrowBlocker = chart.renderer
                  .label(``, 0, 0, 'rectangle')
                  .css({
                    color: tooltipCfg.color,
                    width: tooltipCfg.maxWidth,
                    opacity: 0,
                  })
                  .attr({
                    fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                    height: 3,
                    width: 17,
                    'dominant-baseline': 'auto',
                    'stroke-width': tooltipCfg.borderWidth,
                    'stroke-dasharray':
                      tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                    'stroke-opacity': tooltipCfg.opacity,
                    padding: 2,
                    r: 0,
                    class: 'xLabelArrowBlocker',
                  })
                  .add()
                  .toFront();
                const tooltipRect = chart.xAxisTooltip.getBBox();
                const tooltipBorderWidth = Number(tooltipCfg.borderWidth.replace('px', ''));
                let tooltipX = objRect ? objRect!.x + objRect!.width / 2 - chartRect!.x - tooltipRect.width / 2 : 0;
                if (tooltipX < tooltipBorderWidth) tooltipX = tooltipBorderWidth;
                if (tooltipX + tooltipRect.width > chartRect!.width)
                  tooltipX = chartRect!.width - tooltipRect.width - tooltipBorderWidth;
                chart.xAxisTooltip
                  .attr({
                    x: 100,
                    y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 30 : 0,
                  })
                  .css({
                    opacity: 1,
                  });
                chart.xAxisTooltipArrow
                  .attr({
                    rotation: 45,
                    x: 100 + tooltipRect.width / 2 + 4,
                    y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 55 : 0,
                  })
                  .css({
                    opacity: 1,
                  });
                chart.xAxisTooltipArrowBlocker
                  .attr({
                    x: 100 + tooltipRect.width / 2 - 7,
                    y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 54 : 0,
                  })
                  .css({
                    opacity: 1,
                  });
                const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
                if (htmlObj) htmlObj.style.textShadow = textBoldShadow;
              },
              mouseout: function () {
                const obj = this as unknown as any;
                const value = obj.value.split("'")[3];
                if (labelClicked) return setLabelClicked(false);
                const chart = chartRef.current;
                if (chart.xAxisTooltip) {
                  chart.xAxisTooltip.destroy();
                }
                if (chart.xAxisTooltipArrow) {
                  chart.xAxisTooltipArrow.destroy();
                }
                if (chart.xAxisTooltipArrowBlocker) {
                  chart.xAxisTooltipArrowBlocker.destroy();
                }
                if (value === selectedLabel) return;
                const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
                if (htmlObj) htmlObj.style.textShadow = 'none';
              },
            },
            useHTML: false,
            style: {
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontSize: '14px',
            },
            x: -140,
            align: 'left',
            minWidth: 160,
            width: 160,
          },
        },
        yAxis: {
          endOnTick: false,
          gridZIndex: -1,
          zIndex: -1,
          min: Number(chartYmin) || Number(0),
          max: Number(chartYmax) * 1.1 || Number(maxValue) * 1.1,
          title: {
            text: chartYtitle,
            style: {
              color: 'var(--color-chart-title)',
              fontWeight: 'normal',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
          stackLabels: {
            enabled: false,
          },
        },
        legend: {
          reversed: false,
          x: 2,
          y: 4,
          align: 'left',
          verticalAlign: 'top',
          floating: false,
          backgroundColor: 'white',
          shadow: false,
        },
        tooltip: {
          ...highchartsTooltipCfg(tooltipCfg),
          useHTML: true,
          shape: 'callout',
          backgroundColor: '#fff',
          borderRadius: 5,
          borderColor: 'transparent',
          shadow: true,
          positioner: function (x: any, y: any, plot: any) {
            const { plotY } = plot;
            return { x: 158, y: plotY + 6 };
          },
          formatter: function () {
            const { x, points } = this as unknown as any;
            return `
          <span style="color:#667686;font-weight:500;font-size:16px;line-height:1.5">${x}</span><br />
          <span style="color:${
            points?.[0]?.color
          };border-radius:50%;font-size:18px;line-height:0.2">\u25CF</span> <span style="color:#4C5E72;font-weight:500;font-size:14px;line-height:1.4">${
            points?.[0]?.series?.name
          } ${Humanize.toFixed(points?.[0]?.y, 2).replace('.00', '')}%</span><br />
          ${
            points?.[1]
              ? `<span style="color:${
                  points?.[1]?.color
                };border-radius:50%;font-size:18px;line-height:0.2">\u25CF</span> <span style="color:#4C5E72;font-weight:500;font-size:14px;line-height:1.4">${
                  points?.[1]?.series?.name
                } ${Humanize.toFixed(points?.[1]?.y, 2).replace('.00', '')}%</span>`
              : ''
          }
        `;
          },
        },
        plotOptions: {
          series: {
            animation: animationConfig,
            maxPointWidth: 14,
            pointWidth: 14,
            cursor: 'pointer',
            point: {
              events: {
                click: (e: any) => {
                  const cellInfo = {
                    source: 'value',
                    topic: data.row_nodes[e.point.index].id,
                  };
                  setSelectedLabel(data.row_nodes[e.point.index].id);
                  setLabelClicked(true);
                  onClick(cellInfo);
                },
              },
            },
          },
          bar: {
            borderWidth: 0,
            stacking: 'normal',
            dataLabels: {
              enabled: false,
            },
          },
        },
        series: (seriesData[0].data[0] > 0
          ? seriesData[1]
            ? [
                { ...seriesData[0], borderRadiusTopLeft: '50%', borderRadiusTopRight: '50%', maxWidth: '100%' },
                { ...seriesData[1], borderRadiusTopLeft: '50%', borderRadiusTopRight: '50%', maxWidth: '100%' },
              ]
            : [{ ...seriesData[0], borderRadiusTopLeft: '50%', borderRadiusTopRight: '50%', maxWidth: '100%' }]
          : seriesData[0].data[0] < 0
            ? seriesData.length === 2
              ? [
                  {
                    ...seriesData[seriesData.length - 1],
                    borderRadiusBottomLeft: '50%',
                    borderRadiusBottomRight: '50%',
                    maxWidth: '100%',
                  },
                  {
                    ...seriesData[seriesData.length - 2],
                    borderRadiusBottomLeft: '50%',
                    borderRadiusBottomRight: '50%',
                    maxWidth: '100%',
                  },
                ]
              : [
                  {
                    ...seriesData[seriesData.length - 1],
                    borderRadiusBottomLeft: '50%',
                    borderRadiusBottomRight: '50%',
                    maxWidth: '100%',
                  },
                ]
            : seriesData
        ).map(series => ({
          ...series,
          animation: animationConfig,
        })),
      },
      shouldAnimate,
    };
  }, [
    data,
    size,
    exportFileName,
    onClick,
    chartRef,
    selectedLabel,
    colorScheme,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    prism,
    gridIndex,
    labelClicked,
    gridPrism,
    chartYmax,
    chartYmin,
    firstLoad,
    cameFromDrawer,
    queryStringParams,
    isSizeStable,
  ]);

  const chartOptions = chartMemo?.options;
  const shouldAnimate = chartMemo?.shouldAnimate ?? false;
  const isChartReady = !!chartOptions;

  return (
    <Root $gridheight={gridHeight} id="horizontalStackedBarChart">
      <HighchartContainer>
        {isChartReady ? (
          <HighchartsReact
            containerProps={{ style: { height: '100%' } }}
            style={{ flex: 1 }}
            highcharts={Highcharts}
            constructorType={'chart'}
            options={chartOptions}
            updateArgs={[true, true, shouldAnimate]}
            callback={(chart: any) => (chartRef.current = chart)}
          />
        ) : null}
      </HighchartContainer>
    </Root>
  );
}
